import React, { useState, useEffect, useContext, useRef } from "react";
import { ModalButton } from "../MsgBoxModal/styles";
import { useFormikContext, Field } from "formik";
import {
	GET_CONTACTS_WITH_TAG_PAGED,
	GET_CONTACTS_WITHOUT_TAG_PAGED,
} from "./query";
import client from "../../Apollo";
import { Label, msgBoxSelectCustomStyles } from "../../Styles/global";
import Select from "react-select";
import { useTheme } from "styled-components";
import { useToasts } from "react-toast-notifications";
import { pt } from "date-fns/locale";
import { ObjectCanon } from "@apollo/client/cache/inmemory/object-canon";
import Tooltip from "../Tooltip/tooltip.component";

// Page size for tag-audience recalculation. Large audiences (600+) time out
// (~60s Hasura statement timeout) if fetched in a single unbounded query, so
// every contact fetch below pages through $limit/$offset results of this size.
const PAGE_SIZE = 500;

const FIRST_LAYER_FILTER_OPTIONS = [
	{ label: "Contains", value: "Or" },
	{ label: "Doesn't contain", value: "Isnt" },
];

const SUBSEQUENT_LAYER_FILTER_OPTIONS = [
	{ label: "Or", value: "Or" },
	{ label: "And", value: "And" },
	{ label: "Isnt", value: "Isnt" },
];

const firstLayerTooltip = (
	<div
		style={{
			width: "390px",
			whiteSpace: "pre-line",
			padding: 0,
			margin: 0,
			fontSize: 11,
		}}
	>
		<div style={{ marginBottom: "12px" }}>
			<b style={{ fontSize: "12px", color: "#fff" }}>Contains:</b>
			<br />
			Show contacts that have the selected tag(s). If you pick more than
			one tag, contacts with <i>any</i> of them are included
		</div>
		<div>
			<b style={{ fontSize: "12px", color: "#fff" }}>Doesn't contain:</b>
			<br />
			Show contacts that <i>don't have</i> the selected tag(s). E.g. if
			you pick 'VIP', you'll see all contacts not tagged with 'VIP'
		</div>
	</div>
);

const subsequentLayerTooltip = (
	<div
		style={{
			width: "390px",
			whiteSpace: "pre-line",
			padding: 0,
			margin: 0,
			fontSize: 11,
		}}
	>
		<div style={{ marginBottom: "12px" }}>
			<b style={{ fontSize: "12px", color: "#fff" }}>Or:</b>
			<br />
			Add contacts with <i>any</i> of the tags selected. E.g. if you pick
			"Customer" or "VIP", contacts with either tag will be added
		</div>
		<div style={{ marginBottom: "12px" }}>
			<b style={{ fontSize: "12px", color: "#fff" }}>And:</b>
			<br />
			Narrow to contacts that <i>also</i> have all the selected tags. E.g.
			if you pick 'Customer' and 'VIP', only contacts which have both
			those tags will be kept
		</div>
		<div>
			<b style={{ fontSize: "12px", color: "#fff" }}>Isn't:</b>
			<br />
			Remove contacts that have the selected tags. E.g. if you pick 'VIP',
			any contacts tagged with 'VIP' will be removed
		</div>
	</div>
);

const AdvancedTagDialog = ({
	open,
	setOpen,
	title,
	children,
	MsgBoxSelect,
	canBulkBroadcast,
	loadTagsOptions,
	removedPreSubsCount,
	preSubAsSub,
	setPreSubList,
	setTagSplitVisible,
	preSubCount,
	setPreSubCount,
	tagSplitVisible,
	preSubList,
	advancedSaved,
	setAdvancedSaved,
	advancedSendContacts,
	setAdvancedSendContacts,
	setTagsContactsCount,
}) => {
	const { setFieldValue, values, errors, setFieldTouched } =
		useFormikContext();

	const msgBoxTheme = useTheme();
	const { addToast } = useToasts();

	const [filterLayers, setFilterLayers] = useState(values.tagFilter);
	const [isRecalculating, setIsRecalculating] = useState(false);
	// True when the most recent recalculation failed (GraphQL/network/timeout).
	// While set, the recipient count is unreliable so Save is blocked and the
	// failure is surfaced to the user.
	const [filterError, setFilterError] = useState(false);
	let originalFormValues = useRef();
	// Guards against out-of-order async results: only the most recent
	// filterTags() run is allowed to update state / clear the spinner.
	const filterRequestId = useRef(0);

	useEffect(() => {
		if (open) {
			// Only set if it hasn't been set before or if we just saved successfully
			if (!originalFormValues.current || advancedSaved) {
				originalFormValues.current = structuredClone(filterLayers);
			}
		}
	}, [open, advancedSaved]);

	useEffect(() => {
		setFieldValue("tagFilter", filterLayers);
	}, [filterLayers]);

	useEffect(() => {
		if (filterLayers && values?.account?.value) {
			filterTags();
		}
	}, [filterLayers]);

	async function filterTags() {
		const requestId = ++filterRequestId.current;
		setIsRecalculating(true);
		try {
			// Get the first layer results
			let filteredContacts = (await getContacts(filterLayers[0])) || [];
			// For every subsequent layer, use the previous results as a base
			for (let i = 1; i < filterLayers.length; i++) {
				const layer = filterLayers[i];
				let newContacts =
					(await getContacts(layer, filteredContacts)) || [];
				// Combine newContacts with filteredContacts based on the filter type
				if (layer?.tagFilter?.value === "Isnt") {
					filteredContacts = filteredContacts.filter(
						(contact) =>
							!newContacts.some(
								(nc) => nc.ContactId === contact.ContactId
							)
					);
				} else if (layer?.tagFilter?.value === "And") {
					filteredContacts = filteredContacts.filter((contact) =>
						newContacts.some(
							(nc) => nc.ContactId === contact.ContactId
						)
					);
				} else if (layer?.tagFilter?.value === "Or") {
					filteredContacts = [
						...filteredContacts,
						...newContacts,
					].filter(
						(contact, index, self) =>
							index ===
							self.findIndex(
								(c) => c.ContactId === contact.ContactId
							)
					);
				}
			}
			// Ignore stale results from a superseded run.
			if (requestId === filterRequestId.current) {
				setAdvancedSendContacts(filteredContacts);
				setFilterError(false);
			}
		} catch (e) {
			console.error(e);
			// Only the most recent run may surface its failure / mutate state.
			if (requestId === filterRequestId.current) {
				// Clear the partial audience so Save can't send a recount that
				// silently dropped contacts, and clearly mark the failure.
				setAdvancedSendContacts([]);
				setFilterError(true);
				addToast(
					"Couldn't recalculate recipients - please check your connection and try again.",
					{ appearance: "error", autoDismiss: true }
				);
			}
		} finally {
			if (requestId === filterRequestId.current) {
				setIsRecalculating(false);
			}
		}
	}

	useEffect(() => {
		if (preSubAsSub === false) {
			filterPreSubs();
		}
		if (advancedSendContacts) {
			setTagsContactsCount(advancedSendContacts.length);
		}
	}, [filterLayers, advancedSendContacts]);

	useEffect(() => {
		if (preSubList) setPreSubCount(uniquePreSubs(preSubList));
	}, [preSubList]);

	const addFilterLayer = () => {
		setFilterLayers([
			...filterLayers,
			{
				id: Date.now(),
				tagFilter: { label: "Or", value: "Or" },
				selectedTags: [],
			},
		]);
	};

	const removeFilterLayer = (id) => {
		setFilterLayers(filterLayers.filter((layer) => layer.id !== id));
	};

	const updateFilterLayer = (id, key, value) => {
		setFilterLayers(
			filterLayers.map((layer) =>
				layer.id === id ? { ...layer, [key]: value } : layer
			)
		);
	};

	function handleSave() {
		if (filterLayers[0].selectedTags.length > 0) {
			originalFormValues.current = structuredClone(filterLayers);
			setFieldTouched("tags", true);
			setFieldTouched("tagFilter", true);
			setAdvancedSaved(true);
			setOpen(false);
		} else {
			setOpen(false);
		}
	}

	function handleCancel() {
		if (originalFormValues.current) {
			setFilterLayers(originalFormValues.current);
		} else {
			setFilterLayers([
				{
					id: 0,
					tagFilter: { label: "Or", value: "Or" },
					selectedTags: [],
				},
			]);
		}
		setOpen(false);
	}

	// Page through a contact query that supports $limit/$offset and return the
	// full accumulated list of rows ({ ContactId, Status, Name }). This is what
	// keeps large audiences (600+) from timing out - no single query is ever
	// unbounded, including the account-wide "without tag" query.
	async function pageAllContacts(query, variables) {
		const rows = [];
		let offset = 0;
		// eslint-disable-next-line no-constant-condition
		while (true) {
			const { data } = await client.query({
				query,
				variables: { ...variables, limit: PAGE_SIZE, offset },
				fetchPolicy: "network-only",
			});
			const page = data.msgbox_Contact;
			rows.push(...page);
			if (page.length < PAGE_SIZE) break;
			offset += PAGE_SIZE;
		}
		return rows;
	}

	// Dedupe a contact list by ContactId, keeping first occurrence.
	function dedupeByContactId(contacts) {
		return contacts.filter(
			(contact, index, self) =>
				index ===
				self.findIndex((c) => c.ContactId === contact.ContactId)
		);
	}

	async function getContacts(filterLayer, baseContacts = null) {
		const tags = filterLayer.selectedTags.map((tag) =>
			tag.label.replace(/ *\([^)]*\) *|\*/g, "")
		);
		let resultArr = [];

		if (baseContacts) {
			// When baseContacts exist, perform client-side combining:
			if (filterLayer?.tagFilter?.value === "And") {
				// Intersect: for each tag, retain only contacts in baseContacts that have the tag
				let contactsResult = baseContacts;
				for (const tag of filterLayer.selectedTags) {
					const formattedTag = tag.label
						.replace(/\(\d+\s*contacts\)/gi, "")
						.trim();
					const withTag = await pageAllContacts(
						GET_CONTACTS_WITH_TAG_PAGED,
						{
							tags: [formattedTag],
							account: values.account.value,
						}
					);
					const withTagIds = new Set(
						withTag.map((c) => c.ContactId)
					);
					contactsResult = contactsResult.filter((contact) =>
						withTagIds.has(contact.ContactId)
					);
				}
				resultArr = contactsResult;
			} else {
				// For Or and Isnt, query the contacts and combine with the base list:
				const queryResults = await getTagContacts(
					tags,
					values.account.value,
					filterLayer.tagFilter.value
				);
				if (filterLayer?.tagFilter?.value === "Or") {
					resultArr = dedupeByContactId([
						...baseContacts,
						...queryResults,
					]);
				} else if (filterLayer?.tagFilter?.value === "Isnt") {
					const excludeIds = new Set(
						queryResults.map((q) => q.ContactId)
					);
					resultArr = baseContacts.filter(
						(contact) => !excludeIds.has(contact.ContactId)
					);
				}
			}
		} else {
			// No baseContacts means we're processing the first filter layer:
			if (filterLayer?.tagFilter?.value === "Or") {
				resultArr = await getTagContacts(
					tags,
					values.account.value,
					"Or"
				);
			} else if (filterLayer?.tagFilter?.value === "And") {
				// Intersect the per-tag audiences. Each per-tag query is paged,
				// and the paged rows already carry ContactId/Status/Name, so we
				// no longer need a follow-up GET_PRESUB_FROM_IDS lookup.
				let commonContacts = null;
				const rowsById = new Map();
				for (let i = 0; i < filterLayer.selectedTags.length; i++) {
					let formattedTag = filterLayer.selectedTags[i].label
						.replace(/\(\d+\s*contacts\)/gi, "")
						.trim();
					const withTag = await pageAllContacts(
						GET_CONTACTS_WITH_TAG_PAGED,
						{
							tags: [formattedTag],
							account: values.account.value,
						}
					);
					withTag.forEach((c) => rowsById.set(c.ContactId, c));
					const contacts = new Set(
						withTag.map((c) => c.ContactId)
					);
					commonContacts =
						commonContacts === null
							? contacts
							: new Set(
									[...commonContacts].filter((id) =>
										contacts.has(id)
									)
							  );
				}
				resultArr = [...(commonContacts || new Set())].map((id) =>
					rowsById.get(id)
				);
			} else if (filterLayer?.tagFilter?.value === "Isnt") {
				if (tags.length > 0) {
					resultArr = await getTagContacts(
						tags,
						values.account.value,
						"Isnt"
					);
				}
			}
		}
		return resultArr;
	}

	// Returns the paged list of contact rows for a single filter option. All
	// paths page through results so no query is ever unbounded.
	async function getTagContacts(tags, account, filterOption) {
		if (filterOption === "Or") {
			return await pageAllContacts(GET_CONTACTS_WITH_TAG_PAGED, {
				tags,
				account,
			});
		} else if (filterOption === "Isnt") {
			return await pageAllContacts(GET_CONTACTS_WITHOUT_TAG_PAGED, {
				tags,
				account,
			});
		}
		return [];
	}

	async function filterPreSubs() {
		try {
			setPreSubList([]);
			setTagSplitVisible(false);
			let preSubsInitial = advancedSendContacts.filter((contact) => {
				return contact.Status === "Presubscribed";
			});
			let preSubsFinal = [];
			if (preSubsInitial.length > 10 && preSubAsSub === false) {
				preSubsInitial.map((contact) => {
					preSubsFinal.push({
						label: `Remove "${contact.Name}" from recipients`,
						contactId: contact.ContactId,
						value: {
							contactId: contact.ContactId,
						},
					});
				});
				setPreSubList(preSubsFinal);
				setTagSplitVisible(true);
			}
		} catch (e) {
			console.error(e);
		}
	}

	const handleRemovePreSub = async (selected) => {
		const contactToRemove = preSubList.filter(
			(element) => !selected.includes(element)
		);
		let newSendList = advancedSendContacts.filter((contact) => {
			return contact.ContactId !== contactToRemove[0].contactId;
		});
		setAdvancedSendContacts(newSendList);
	};

	// Handle closing when clicking outside the dialog
	const handleOutsideClick = (e) => {
		if (e.target.tagName === "DIALOG") {
			setOpen(false);
		}
	};

	// The first row establishes the starting audience, so And/Or (which describe
	// how a row combines with the previous one) make no sense there - it can only
	// include ("Contains") or exclude ("Doesn't contain") the selected tag(s).
	// Additional rows then refine that audience with Or / And / Isn't.
	const getFilterOptions = (index) =>
		index === 0
			? FIRST_LAYER_FILTER_OPTIONS
			: SUBSEQUENT_LAYER_FILTER_OPTIONS;

	// Resolve the option to display from the stored value so the label always
	// matches the options available for that row (e.g. value "Or" -> "Contains"
	// on the first row).
	const getFilterValue = (layer, index) => {
		const options = getFilterOptions(index);
		return (
			options.find(
				(option) => option.value === layer?.tagFilter?.value
			) || options[0]
		);
	};

	function uniquePreSubs(contactList) {
		let uniqueIds = new Set();
		contactList.forEach((preSub) => {
			if (preSub.contactId != null) {
				// Ensure contactId is valid
				uniqueIds.add(preSub.contactId);
			}
		});
		return uniqueIds.size; // Return count of unique IDs
	}

	return (
		<dialog
			open={open}
			onClick={handleOutsideClick}
			style={{
				border: "1px solid",
				background: "white",
				padding: "20px",
				minWidth: "95%",
				borderRadius: "8px",
				position: "fixed",
				top: "50%",
				left: "50%",
				transform: "translate(-50%, -50%)",
				zIndex: 1000,
				maxHeight: 500,
				overflowY: "scroll",
			}}
		>
			<div className="dialog-content">
				<h2>{title}</h2>
				{children}

				<div>
					<div>
						{filterLayers.map((layer, index) => (
							<div
								key={layer.id}
								style={{
									display: "flex",
									flexDirection: "row",
									gap: 10,
									alignItems: "center",
									justifyContent: "space-between",
									whiteSpace: "pre-line",
								}}
							>
								<Field
									component={MsgBoxSelect}
									name="tagFilter"
									label="Filter"
									hasTooltip
									toolTipText={
										index === 0
											? firstLayerTooltip
											: subsequentLayerTooltip
									}
									options={getFilterOptions(index)}
									value={getFilterValue(layer, index)}
									onChange={(selected) => {
										updateFilterLayer(
											layer.id,
											"tagFilter",
											selected
										);
										setFieldValue(
											"tags",
											filterLayers[0].selectedTags
										);
									}}
								/>

								<Field
									component={MsgBoxSelect}
									name="tags"
									label="Select Tag(s) "
									isMulti
									loadOptions={loadTagsOptions} // Proper async loading
									placeholder="Search tags"
									value={layer.selectedTags}
									onChange={(selected) => {
										let total = selected.reduce(
											(acc, item) => acc + item.userCount,
											0
										);
										let newOptions = [...selected];

										if (!canBulkBroadcast && total > 10) {
											newOptions.pop();
										}

										updateFilterLayer(
											layer.id,
											"selectedTags",
											newOptions
										);
									}}
									onBlur={() => {
										setFieldTouched("tags", true);
									}}
									error={errors.tags}
									async
								/>

								{filterLayers.length > 1 && (
									<span
										onClick={() =>
											removeFilterLayer(layer.id)
										}
									>
										➖
									</span>
								)}
							</div>
						))}

						<span onClick={addFilterLayer}>➕</span>
					</div>

					{filterLayers[0].selectedTags.length > 0 ? (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								marginRight: 10,
								marginBottom: 25,
								marginTop: 25,
							}}
						>
							{isRecalculating ? (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
									}}
								>
									<i className="fas fa-spinner fa-spin"></i>
									<span>Recalculating recipients…</span>
								</div>
							) : filterError ? (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
										color: "red",
									}}
								>
									<i className="fas fa-exclamation-triangle"></i>
									<span>
										Couldn't recalculate recipients. Please
										try again.
									</span>
								</div>
							) : (
								<>
									<b>
										Total:{" "}
										{advancedSendContacts.length -
											removedPreSubsCount}
										{" contacts "}
									</b>

									{preSubList?.length > 10 ? (
										<p
											style={{
												color:
													preSubList.length > 10
														? "red"
														: "black",
											}}
										>
											Presubscribers: {preSubCount}{" "}
										</p>
									) : null}

									<p style={{ fontSize: 9 }}>
										* excludes any duplicates
									</p>
								</>
							)}
						</div>
					) : null}
				</div>

				{tagSplitVisible === true ? (
					<>
						<Label style={{ color: "red", fontSize: "10px" }}>
							Your current setup limits you to sending to 10
							presubscribers, please remove some to continue to
							send this broadcast.
						</Label>
						<div
							style={{
								maxHeight: "150px",
								overflow: "auto",
							}}
						>
							<Select
								isMulti
								isClearable={false}
								value={preSubList}
								onChange={handleRemovePreSub}
								styles={msgBoxSelectCustomStyles(msgBoxTheme)}
								theme={(theme) => ({
									...theme,
									colors: {
										...theme.colors,
										primary25:
											msgBoxTheme.colours.primaryLight,
										primary: msgBoxTheme.colours.primary,
									},
								})}
							></Select>
						</div>{" "}
					</>
				) : null}

				<div
					style={{
						fontSize: 11,
						display: "flex",
						justifyContent: "center",
						margin: 20,
					}}
				>
					<p>
						<u>First filter</u> - "Contains" includes contacts with
						the selected tag(s); "Doesn't contain" excludes them.{" "}
						<br />
						<u>Additional filters</u> - Or adds contacts in{" "}
						<b>any</b> selected tags; And narrows to contacts in{" "}
						<b>all</b> selected tags; Isn't removes contacts that{" "}
						<b>have</b> the selected tags.
					</p>
				</div>

				<div
					style={{
						marginTop: 50,
						display: "flex",
						alignItems: "center",
						maxWidth: 200,

						position: "relative",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
					}}
				>
					<ModalButton
						buttonName="Cancel"
						secondary
						onClick={() => {
							handleCancel();
						}}
					></ModalButton>
					<ModalButton
						type="button"
						buttonName="Save"
						primary
						loading={isRecalculating}
						disabled={isRecalculating || filterError}
						onClick={() => {
							// Block save while recalculating or after a failed
							// recount so a partial/failed audience is never saved.
							if (isRecalculating || filterError) return;
							handleSave();
						}}
					></ModalButton>
				</div>
			</div>
		</dialog>
	);
};

export default AdvancedTagDialog;
